/*
 * Tutorial videos: one JSON file each, written by apps/web/scripts/videos
 * (make-video.ts, then publish.sh, which also uploads the files to
 * /media/videos/<slug>/). Narrated with an AI voice, which every page says.
 */
import type { GuideAudience } from '../guides';

export interface Video {
  slug: string;
  title: string;
  summary: string;
  audiences: GuideAudience[];
  /** Guide chapters it belongs with: "chapter" (in any guide) or "guide/chapter". */
  chapters: string[];
  order: number;
  /** Length in seconds. */
  duration: number;
  /** When it was recorded (YYYY-MM-DD). */
  made: string;
  /** The narration, a sentence at a time, with when each starts (seconds). */
  transcript: { start: number; text: string }[];
}

const files = import.meta.glob<Video>('./*.json', { eager: true, import: 'default' });

export const VIDEOS: Video[] = Object.values(files).sort((a, b) => a.order - b.order);

export const findVideo = (slug: string | undefined) => VIDEOS.find((v) => v.slug === slug);

export function videoFiles(slug: string) {
  const base = `/media/videos/${slug}/${slug}`;
  return {
    hd: `${base}.mp4`,
    sd: `${base}-480p.mp4`,
    captions: `${base}.vtt`,
    poster: `${base}.jpg`,
  };
}

/** Videos that belong with a guide's chapter. */
export const videosFor = (guide: string, chapter: string) =>
  VIDEOS.filter((v) => v.chapters.some((c) => c === chapter || c === `${guide}/${chapter}`));

/** "1:09" */
export function formatDuration(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

/** Videos whose title, summary or narration has every word of `query`, best first. */
export function searchVideos(query: string): Video[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return VIDEOS.map((v) => {
    const title = v.title.toLowerCase();
    const rest = `${v.summary} ${v.transcript.map((t) => t.text).join(' ')}`.toLowerCase();
    let score = 0;
    for (const w of words) {
      const s = (title.includes(w) ? 6 : 0) + (rest.includes(w) ? 1 : 0);
      if (!s) return { v, score: 0 };
      score += s;
    }
    return { v, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.v);
}
